/* eslint-disable react/jsx-no-constructed-context-values */

'use client';

import React, { useCallback, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Outlet } from 'react-router-dom';
import { Loader } from '@perxis-auto/ui-kit';

import ErrorBoundaryFallback from '@/components/ErrorBoundaryFallback/ErrorBoundaryFallback';
import { ConstructorContext } from '@/context/constructor';
import getConstructorItemsByPath from '@/features/config/helpers/getConstructorItemsByPath';
import useFilterConstructor from '@/features/config/hooks/useFilterConstructor';
import { useAppSelector } from '@/features/hooks';
import { rootSelectors } from '@/features/store';
import useCurrentPage from '@/hooks/useCurrentPage';
import { IPossibleConstructorGridItem } from '@/interfaces/constructor.interfaces';

import { ConstructorGrid, ConstructorItem } from './components';
import { ConstructorLayoutProps } from './Constructor.types';

function ConstructorLayout(props: ConstructorLayoutProps) {
  const currentPage = useCurrentPage();

  const constructorConfig = useAppSelector(rootSelectors.config.constructorSelector);

  const pageConstructorConfig = getConstructorItemsByPath(constructorConfig, currentPage);

  const pageConstructor = useFilterConstructor(pageConstructorConfig);

  const renderConstructorItem = useCallback(
    (constructorItem: IPossibleConstructorGridItem) => {
      return <ConstructorItem item={constructorItem} />;
    },
    [],
  );

  const children = useMemo(() => {
    if (!pageConstructor) {
      return null;
    }

    return (
      <React.Suspense fallback={<Loader className="main-loader" size="large" />}>
        <ConstructorContext.Provider value={{ grid: pageConstructor }}>
          <ConstructorGrid grid={pageConstructor} renderItem={renderConstructorItem} />
        </ConstructorContext.Provider>
      </React.Suspense>
    );
  }, [pageConstructor, renderConstructorItem]);

  return (
    <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
      <Outlet context={{ children }} />
    </ErrorBoundary>
  );
}

export default React.memo(ConstructorLayout);
